import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom, Observable } from 'rxjs';
import UniqueEntityID from 'src/app/core/domain/unique_entity_id';
import {
  GenericException,
  UnAuthorizedException,
} from 'src/app/core/logic/exception';
import { environment } from 'src/environments/environment';

import {
  FieldTool,
  FieldType,
} from '../../../ui/template/routes/editor/components/fields-library/field_type';
import { SharedDocumentId } from '../../document/domain/shared_document/shared_document';
import { PatientId } from '../../patient/domain/patient';
import { ReportAiEventDetail } from '../../transcription/domain/events/report-ai-events';
import { DietitianId } from '../domain/dietitian';
import { Note, NoteId, NoteReportProps } from '../domain/note/note';
import { NoteNotFoundException } from '../domain/note/note_exceptions';
import { NoteField } from '../domain/note/note_field';
import { TemplateId } from '../domain/templates/template';
import { TemplateFieldId } from '../domain/templates/template_field';

export interface NoteReportSchema {
  code: string;
  label: string;
  value: any;
  inputTokens: number | undefined;
  outputTokens: number | undefined;
}

export interface NoteSchema {
  patientId: string;
  notesTemplateId: string;
  name: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  fields?: NoteFieldSchema[];
  sharedDocumentId: string | undefined;
  sharedAt: Timestamp | undefined;
  reports: NoteReportSchema[] | null;
  datedAt: Timestamp | null;
}

export interface PendingNoteRecord {
  noteId: string;
  dietitianId: string;
  schema: NoteSchema;
}

export interface NoteFieldSchema {
  id: string;
  notesTemplateFieldId: string;
  label: string;
  updatedAt: Timestamp;
  type: FieldType;
  params: unknown | null;
  value: unknown | null;
  serieItemId: string | null;
  order: number;
}

@Injectable()
export class NoteRepository {
  private static readonly PENDING_NOTES_STORAGE_KEY = 'msd.notes.pending';

  private readonly noteReportCloud: (data: {
    dietitianId: string;
    patientId: string;
    noteId: string;
    templateId: string;
  }) => Observable<ReportAiEventDetail | undefined>;

  constructor(
    private firestore: AngularFirestore,
    private functions: AngularFireFunctions,
    private fireAuth: AngularFireAuth,
    private http: HttpClient,
  ) {
    this.noteReportCloud = this.functions.httpsCallable<
      {
        dietitianId: string;
        patientId: string;
        noteId: string;
        templateId: string;
      },
      any
    >('note-noteReport');
  }

  private collection(dietitianId: string, queryFn?: QueryFn) {
    return this.firestore
      .collection('dietitians')
      .doc(dietitianId)
      .collection<NoteSchema>('notes', queryFn);
  }

  toSchema(note: Note): NoteSchema {
    return <NoteSchema>{
      patientId: note.patientId.id.toString(),
      notesTemplateId: note.templateId.id.toString(),
      name: note.name,
      createdAt:
        note.createdAt !== undefined
          ? Timestamp.fromDate(note.createdAt)
          : Timestamp.now(),
      updatedAt: Timestamp.now(),
      fields: note.fields
        .map(
          (f) =>
            <NoteFieldSchema>{
              id: f.id.toString(),
              notesTemplateFieldId: f.notesTemplateFieldId.id.toString(),
              label: f.label,
              updatedAt: Timestamp.now(),
              type: f.type,
              params: f.params ?? null,
              value: FieldTool.valueToSchema(f.type, f.value),
              serieItemId: f.serieItemId || null,
              order: f.order,
            },
        )
        .sort((a, b) => a.order - b.order),
      sharedDocumentId: note.sharedDocumentId?.id.toString() ?? null,
      sharedAt:
        note.sharedAt !== undefined ? Timestamp.fromDate(note.sharedAt) : null,
      reports: note.reports ? this.toReportsSchema(note.reports) : null,
      datedAt: note.datedAt ? Timestamp.fromDate(note.datedAt) : null,
    };
  }

  toReportSchema(report: NoteReportProps): NoteReportSchema {
    return <NoteReportSchema>{
      code: report.code,
      label: report.label,
      value: report?.value ?? undefined,
      inputTokens: report?.inputTokens ?? undefined,
      outputTokens: report?.outputTokens ?? undefined,
    };
  }

  fromSchema(schema: NoteSchema, dietitianId: string, id: string): Note {
    return Note.create(
      {
        dietitianId: DietitianId.create(new UniqueEntityID(dietitianId)),
        patientId: PatientId.create(new UniqueEntityID(schema.patientId)),
        templateId: TemplateId.create(
          new UniqueEntityID(schema.notesTemplateId),
        ),
        name: schema.name,
        createdAt: schema.createdAt.toDate(),
        updatedAt: schema.updatedAt.toDate(),
        fields:
          schema.fields
            ?.map((f) =>
              NoteField.create(
                {
                  noteId: NoteId.create(new UniqueEntityID(id)),
                  notesTemplateFieldId: TemplateFieldId.create(
                    new UniqueEntityID(f.notesTemplateFieldId),
                  ),
                  label: f.label,
                  updatedAt: f.updatedAt.toDate(),
                  type: f.type,
                  params: f.params,
                  value: FieldTool.valueFromSchema(f.type, f.value),
                  serieItemId: f.serieItemId ?? undefined,
                  order: f.order,
                },
                new UniqueEntityID(f.id),
              ),
            )
            .sort((a, b) => a.order - b.order) ?? [],
        sharedDocumentId: schema.sharedDocumentId
          ? SharedDocumentId.create(new UniqueEntityID(schema.sharedDocumentId))
          : undefined,
        sharedAt: schema.sharedAt?.toDate() ?? undefined,
        reports: schema.reports
          ? this.fromReportsSchema(schema.reports)
          : undefined,
        datedAt: schema.datedAt?.toDate(),
      },
      new UniqueEntityID(id),
    );
  }

  async noteReport(
    dietitianId: string,
    patientId: string,
    noteId: string,
    templateId: string,
  ): Promise<ReportAiEventDetail | undefined> {
    return await firstValueFrom(
      this.noteReportCloud({
        dietitianId,
        patientId,
        noteId,
        templateId,
      }),
    );
  }

  private toReportsSchema(
    reports: NoteReportProps[] | undefined,
  ): NoteReportSchema[] | null {
    if (!reports) {
      return null;
    }
    return reports.map((r) => this.toReportSchema(r));
  }

  private fromReportSchema(report: NoteReportSchema): NoteReportProps {
    return <NoteReportProps>{
      code: report.code,
      label: report.label,
      value: report?.value ?? undefined,
      inputTokens: report?.inputTokens ?? undefined,
      outputTokens: report?.outputTokens ?? undefined,
    };
  }

  async load(dietitianId: string, noteId: string): Promise<Note> {
    const snap = await firstValueFrom(
      this.collection(dietitianId).doc(noteId).get(),
    );
    if (!snap.exists || snap.data == null) {
      throw new NoteNotFoundException();
    }

    return this.fromSchema(snap.data() as NoteSchema, dietitianId, snap.id);
  }

  async existsForDietitianIdTemplateId(
    dietitianId: string,
    templateId: string,
  ): Promise<boolean> {
    const snap = await firstValueFrom(
      this.collection(dietitianId, (ref) =>
        ref.where('notesTemplateId', '==', templateId).limit(1),
      ).get(),
    );

    return snap.size > 0;
  }

  async findByDietitianId(dietitianId: string, asc = false): Promise<Note[]> {
    const snap = await firstValueFrom(
      this.collection(dietitianId, (ref) =>
        ref.orderBy('createdAt', asc ? 'asc' : 'desc'),
      ).get(),
    );

    return snap.docs.map((doc) =>
      this.fromSchema(doc.data(), dietitianId, doc.id),
    );
  }

  async findByDietitianAndPatientId(
    dietitianId: string,
    patientId: string,
    asc = false,
  ): Promise<Note[]> {
    const snap = await firstValueFrom(
      this.collection(dietitianId, (ref) =>
        ref
          .where('patientId', '==', patientId)
          .orderBy('createdAt', asc ? 'asc' : 'desc'),
      ).get(),
    );

    return snap.docs.map((doc) =>
      this.fromSchema(doc.data(), dietitianId, doc.id),
    );
  }

  async create(note: Note): Promise<Note> {
    const schema = this.toSchema(note);
    const dietitianId = note.dietitianId.id.toString();
    const id = note.noteId.id.toString();
    await this.collection(dietitianId).doc(id).set(schema);
    return this.fromSchema(schema, dietitianId, id);
  }

  savePendingNote(note: Note): void {
    const pending = this.readPendingNotes();
    const id = note.noteId.id.toString();
    pending[id] = {
      noteId: id,
      dietitianId: note.dietitianId.id.toString(),
      schema: this.toSchema(note),
    };
    this.writePendingNotes(pending);
  }

  clearPendingNote(noteId: string): void {
    const pending = this.readPendingNotes();
    delete pending[noteId];
    this.writePendingNotes(pending);
  }

  getPendingNotes(dietitianId: string): Note[] {
    return Object.values(this.readPendingNotes())
      .filter((record) => record.dietitianId === dietitianId)
      .map((record) =>
        this.fromSchema(record.schema, record.dietitianId, record.noteId),
      );
  }

  private readPendingNotes(): Record<string, PendingNoteRecord> {
    try {
      const raw = localStorage.getItem(
        NoteRepository.PENDING_NOTES_STORAGE_KEY,
      );
      return raw
        ? (JSON.parse(raw, NoteRepository.timestampReviver) as Record<
            string,
            PendingNoteRecord
          >)
        : {};
    } catch {
      return {};
    }
  }

  private writePendingNotes(pending: Record<string, PendingNoteRecord>): void {
    try {
      localStorage.setItem(
        NoteRepository.PENDING_NOTES_STORAGE_KEY,
        JSON.stringify(pending, NoteRepository.timestampReplacer),
      );
    } catch {
      // On ignore, la note reste protégée uniquement par le
      // comportement standard de Firestore dans ce cas.
    }
  }

  private static timestampReplacer(_key: string, value: unknown): unknown {
    if (value instanceof Timestamp) {
      return {
        __timestamp__: true,
        seconds: value.seconds,
        nanoseconds: value.nanoseconds,
      };
    }
    return value;
  }

  private static timestampReviver(_key: string, value: unknown): unknown {
    if (value && typeof value === 'object') {
      const { seconds, nanoseconds } = value as {
        seconds: number;
        nanoseconds: number;
      };
      if (seconds && nanoseconds) {
        return new Timestamp(seconds, nanoseconds);
      }
    }
    return value;
  }

  async save(note: Note): Promise<Note> {
    const schema = this.toSchema(note);
    return this.collection(note.dietitianId.id.toString())
      .doc(note.noteId.id.toString())
      .set(schema)
      .then(() =>
        this.fromSchema(
          schema,
          note.dietitianId.id.toString(),
          note.noteId.id.toString(),
        ),
      );
  }

  async delete(dietitianId: string, noteId: string): Promise<void> {
    return this.collection(dietitianId).doc(noteId).delete();
  }

  async generatePdf(noteId: string): Promise<Blob> {
    const currentUser = await this.fireAuth.currentUser;
    const token = currentUser ? await currentUser.getIdToken() : undefined;
    if (!token) {
      throw new UnAuthorizedException();
    }

    let result: HttpResponse<Blob>;
    try {
      result = await firstValueFrom(
        this.http.post(
          environment.firebaseFunctionsUrl + 'note-generateNotePdf',
          {
            noteId,
          },
          {
            headers: new HttpHeaders({
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              Accept: 'application/pdf, application/json',
            }),
            observe: 'response',
            responseType: 'blob',
          },
        ),
      );
    } catch (error: unknown) {
      if (
        error instanceof HttpErrorResponse &&
        error.error instanceof Blob &&
        error.error.type == 'application/json'
      ) {
        const stJson = await error.error.text();
        const json = stJson
          ? JSON.parse(stJson)
          : { message: "une erreur s'est produite" };
        throw new GenericException(json.message);
      }
      throw new GenericException(error);
    }

    if (!result.ok) {
      throw new GenericException(result.statusText);
    }

    if (!result.body) {
      throw new GenericException();
    }

    if (result.body.type == 'application/pdf') {
      return result.body;
    }

    if (result.body.type == 'application/json') {
      const stJson = await result.body.text();
      const json = stJson
        ? JSON.parse(stJson)
        : { message: "une erreur s'est produite" };
      throw new GenericException(json.message);
    }

    throw new GenericException();
  }

  async generateReportPdf(noteId: string, code: string): Promise<Blob> {
    const currentUser = await this.fireAuth.currentUser;
    const token = currentUser ? await currentUser.getIdToken() : undefined;
    if (!token) {
      throw new UnAuthorizedException();
    }

    let result: HttpResponse<Blob>;
    try {
      result = await firstValueFrom(
        this.http.post(
          environment.firebaseFunctionsUrl + 'note-generateNoteReportPdf',
          {
            noteId,
            code,
          },
          {
            headers: new HttpHeaders({
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              Accept: 'application/pdf, application/json',
            }),
            observe: 'response',
            responseType: 'blob',
          },
        ),
      );
    } catch (error: unknown) {
      if (
        error instanceof HttpErrorResponse &&
        error.error instanceof Blob &&
        error.error.type == 'application/json'
      ) {
        const stJson = await error.error.text();
        const json = stJson
          ? JSON.parse(stJson)
          : { message: "une erreur s'est produite" };
        throw new GenericException(json.message);
      }
      throw new GenericException(error);
    }

    if (!result.ok) {
      throw new GenericException(result.statusText);
    }

    if (!result.body) {
      throw new GenericException();
    }

    if (result.body.type == 'application/pdf') {
      return result.body;
    }

    if (result.body.type == 'application/json') {
      const stJson = await result.body.text();
      const json = stJson
        ? JSON.parse(stJson)
        : { message: "une erreur s'est produite" };
      throw new GenericException(json.message);
    }

    throw new GenericException();
  }

  async newNoteId(dietitianId: string): Promise<string> {
    const snap = await firstValueFrom(this.collection(dietitianId).doc().get());
    return snap.id;
  }

  private fromReportsSchema(
    reports: NoteReportSchema[] | null,
  ): NoteReportProps[] | undefined {
    if (!reports) {
      return undefined;
    }
    return reports.map((r) => this.fromReportSchema(r));
  }
}
